import { Node, mergeAttributes } from '@tiptap/core';
import { EmbedProvider, isAllowedEmbedSrc } from '../embedProviders';

export interface EmbedAttrs {
  src: string;
  provider: EmbedProvider | string;
  originalUrl?: string | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    embed: {
      setEmbed: (attrs: EmbedAttrs) => ReturnType;
    };
  }
}

export const Embed = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      provider: { default: 'youtube' },
      originalUrl: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.cms-embed',
        getAttrs: (node) => {
          const el = node as HTMLElement;
          const iframe = el.querySelector('iframe');
          const src = iframe?.getAttribute('src');
          if (!src || !isAllowedEmbedSrc(src)) return false;
          return {
            src,
            provider: el.getAttribute('data-provider') || 'youtube',
            originalUrl: el.getAttribute('data-original-url'),
          };
        },
      },
      {
        tag: 'iframe[src]',
        getAttrs: (node) => {
          const el = node as HTMLElement;
          if (el.closest('div.cms-embed')) return false;
          const src = el.getAttribute('src');
          if (!src || !isAllowedEmbedSrc(src)) return false;
          return { src, provider: 'youtube', originalUrl: null };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { src, provider, originalUrl } = node.attrs as EmbedAttrs;
    if (!src || !isAllowedEmbedSrc(src)) {
      return ['div', { class: 'cms-embed cms-embed-invalid' }, 'Unsupported embed'];
    }
    const wrap: Record<string, string> = {
      class: 'cms-embed',
      'data-provider': String(provider || 'youtube'),
    };
    if (originalUrl) wrap['data-original-url'] = originalUrl;

    return [
      'div',
      wrap,
      [
        'iframe',
        mergeAttributes({
          src,
          width: '560',
          height: '315',
          loading: 'lazy',
          allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
          allowfullscreen: 'true',
          frameborder: '0',
        }),
      ],
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const { src, provider } = node.attrs as EmbedAttrs;
      const dom = document.createElement('div');
      dom.className = 'cms-embed';
      dom.setAttribute('data-provider', String(provider || 'youtube'));
      dom.contentEditable = 'false';

      if (!src || !isAllowedEmbedSrc(src)) {
        dom.classList.add('cms-embed-invalid');
        dom.textContent = 'Unsupported embed';
        return { dom };
      }

      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.setAttribute('loading', 'lazy');
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      );
      iframe.setAttribute('frameborder', '0');
      iframe.title = `${provider} embed`;
      dom.appendChild(iframe);
      return { dom };
    };
  },

  addCommands() {
    return {
      setEmbed:
        (attrs) =>
        ({ commands }) => {
          if (!attrs.src || !isAllowedEmbedSrc(attrs.src)) return false;
          return commands.insertContent({ type: this.name, attrs });
        },
    };
  },
});

export default Embed;
